import { ArrowUpRight } from "lucide-react";

import { ContentLink } from "@/components/core/content-link";
import { PageContainer } from "@/components/core/page-container";
import { useLocale } from "@/i18n/locale-context";
import type { ProjectEntry } from "@/types/site";

interface ProjectsSectionProps {
  items: ProjectEntry[];
}

/**
 * Presents independent projects as direct first-person summaries without
 * critique-style framing.
 */
export function ProjectsSection({ items }: ProjectsSectionProps): JSX.Element {
  const { locale } = useLocale();
  const roleLabel = locale === "es" ? "Mi rol" : "My role";

  return (
    <section className="pb-20">
      <PageContainer>
        <div className="grid gap-0">
          <div className="spotify-divider" />
          {items.map((item) => (
            <article
              className="grid gap-6 py-8 lg:grid-cols-[0.38fr,0.62fr]"
              key={item.name}
            >
              <div className="space-y-3">
                <h2 className="text-2xl font-bold text-foreground md:text-3xl">
                  {item.name}
                </h2>
                <p className="max-w-lg text-base leading-7 text-foreground">
                  {item.shortDescription}
                </p>
                <p className="text-sm leading-7 text-spotify-silver">
                  {item.status}
                </p>
              </div>

              <div className="space-y-5">
                <p className="max-w-2xl text-sm leading-8 text-spotify-silver md:text-base">
                  {item.summary}
                </p>
                <div className="space-y-2">
                  <p className="text-xs font-bold uppercase tracking-[1.4px] text-muted-foreground">
                    {roleLabel}
                  </p>
                  <p className="text-sm leading-7 text-spotify-silver md:text-base">
                    {item.role}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {item.technologies.map((technology) => (
                    <span
                      className="rounded-full-pill border border-spotify-border bg-spotify-surface-light px-3 py-1.5 text-xs font-bold uppercase tracking-[1.4px] text-spotify-silver"
                      key={technology}
                    >
                      {technology}
                    </span>
                  ))}
                </div>
                {item.links?.length ? (
                  <div className="flex flex-wrap gap-3">
                    {item.links.map((link) => (
                      <ContentLink
                        className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[1.4px] text-primary transition hover:text-primary/80"
                        download={link.download}
                        href={link.href}
                        key={`${item.name}-${link.label}`}
                      >
                        {link.label}
                        <ArrowUpRight aria-hidden="true" className="size-4" />
                      </ContentLink>
                    ))}
                  </div>
                ) : null}
              </div>

              <div className="col-span-full spotify-divider" />
            </article>
          ))}
        </div>
      </PageContainer>
    </section>
  );
}
